import * as logging from 'utils/Logging'

import { borrowerApiClient } from 'api/BorrowerApiClient'
import { Feature } from 'api/BorrowerApiSchemas'
import { useCallback, useState } from 'react'

interface FetchFeaturesProps {
  features: Feature[]
  fetchFeatures: () => void
}

/**
 * ConfigCat flags for the signed-in customer.
 *
 * There is no ConfigCat SDK in the portal — CP API resolves the flags (`Config.featuresFor`) and
 * returns them on `AccountDetails.features`, so this reads them from there rather than adding a
 * client-side SDK and a public SDK key.
 *
 * A failure leaves the list empty, which reads as "every flag off". That is the safe direction:
 * a flag outage falls back to the behaviour the flag was introduced to gate.
 */
const useFeaturesApiRequest = (): FetchFeaturesProps => {
  // Built during render, never inside the callback: borrowerApiClient() defaults its argument to
  // `oktaWrapper.useOktaAuth().oktaAuth`, so calling it is itself a hook call. Reaching for it
  // from inside fetchFeatures throws "Invalid hook call" and takes down every authenticated page.
  const borrowerApi = borrowerApiClient()
  const [features, setFeatures] = useState<Feature[]>([])

  const fetchFeatures = useCallback(() => {
    borrowerApi.getAccountDetails().then(
      (accountDetails) => {
        setFeatures(accountDetails.features ?? [])
      },
      (error) => {
        logging.logError('fetchFeatures', error)
        setFeatures([])
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { features, fetchFeatures }
}

export default useFeaturesApiRequest
